import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Style
import {
  MainWrapper,
  TopWrapper,
  MainWrapperFavorite,
} from "../../style/DasboardMain";
import "react-tabs/style/react-tabs.css";

// Components
import Navigation from "../../components/Navigation/Navigation";
import IntroText from "../../components/introText/index";
import Button from "../../components/button/index";
import Search from "../../components/inputs/Search";

import {
  ClubCard,
  ClubContent,
  ClubsPlayersWrapper,
  ClubsWrapper,
  EmptySearch,
  EmptyState,
  Location,
  NumbersStat,
  PlayersCount,
  SearchInputWrapper,
  SearchItem,
  SearhWrap,
  SeeMoreTeam,
  TableNamesTop,
  TeamContent,
  TeamsCard,
  TeamsWrapp,
  TeamsWrapper,
  TopStatCard,
  TopStatisticsWrapper,
  TopTable,
} from "../../style/Tournaments";

// Images
import next from "../../images/ic_next.svg";
import next_blue from "../../images/next_blue.svg";
import emptyState from "../../images/ic_empty.svg";
import searchIcon from "../../images/ic_search.svg";
import closeDropdown from "../../images/up.svg";

import { $apiV1, $apiV2 } from "../../config/api";
import { useLexicon } from "../../hooks/useLexicon";
import { getMediaUrl } from "../../helpers";
import {
  trackEntitySearch,
  trackEntitySearchResultSelected,
} from "../../tracking";

const Index = () => {
  const [topStat, setTopStat] = useState("");
  const [clubsList, setClubsList] = useState([]);
  const [clubsForLeague, setClubsForLeague] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredClubs, setFilteredClubs] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedCompetitionID, setSelectedCompetitionID] = useState();
  const seasonIdFromQuery = searchParams.get("seasonId");

  const l = useLexicon();
  const searchAbortRef = useRef(null);
  const initialLoadAbortRef = useRef(null);

  const isTruthySeasonFlag = (value) =>
    value === true || value === "true" || value === 1 || value === "1";

  const getSeasonValue = (season, keys) => {
    for (const key of keys) {
      if (season?.[key] !== undefined && season?.[key] !== null && season?.[key] !== "") {
        return season[key];
      }
      if (
        season?.year_season?.[key] !== undefined &&
        season?.year_season?.[key] !== null &&
        season?.year_season?.[key] !== ""
      ) {
        return season.year_season[key];
      }
    }
    return null;
  };

  const parseSeasonDate = (value) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const getSeasonLabel = (season) => {
    const rawYearSeason = season?.year_season || season?.yearSeason;
    if (rawYearSeason) {
      return String(rawYearSeason).replace(/\s+/g, "");
    }

    const rawTitle = season?.title || season?.shortTitle || "";
    const rangeMatch = String(rawTitle).match(/((?:19|20)\d{2})\s*[-/]\s*((?:19|20)\d{2})/);
    if (rangeMatch) {
      return `${rangeMatch[1]}-${rangeMatch[2]}`;
    }

    const yearMatch = String(rawTitle).match(/\b((?:19|20)\d{2})\b/);
    if (yearMatch) {
      return yearMatch[1];
    }

    return `${season?.id || ""}`;
  };

  const getPreferredSeason = (seasons = []) => {
    const seasonsList = Array.isArray(seasons)
      ? seasons.filter((season) => season?.id !== undefined && season?.id !== null)
      : [];

    if (!seasonsList.length) return null;

    const activeCandidates = seasonsList.filter((season) =>
      ["is_active", "active", "is_current", "current"].some((flag) =>
        isTruthySeasonFlag(getSeasonValue(season, [flag]))
      )
    );

    if (activeCandidates.length > 0) {
      return activeCandidates.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0))[0];
    }

    const now = new Date();
    const startedCandidates = seasonsList
      .map((season) => {
        const startDate = parseSeasonDate(
          getSeasonValue(season, ["start_date", "date_start", "start", "starts_at"])
        );
        return { season, startDate };
      })
      .filter(({ startDate }) => !startDate || startDate <= now)
      .sort((a, b) => {
        const aStart = a.startDate ? a.startDate.getTime() : Number.MIN_SAFE_INTEGER;
        const bStart = b.startDate ? b.startDate.getTime() : Number.MIN_SAFE_INTEGER;
        if (aStart !== bStart) {
          return bStart - aStart;
        }
        return Number(b?.season?.id || 0) - Number(a?.season?.id || 0);
      });

    if (startedCandidates.length > 0) {
      return startedCandidates[0].season;
    }

    const byIdDesc = [...seasonsList].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
    return byIdDesc[0] || seasonsList[0];
  };

  const getSortedSeasons = (seasons = []) =>
    [...(seasons || [])].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));

  const getLeagueName = (club) => {
    const competitionName = String(club?.name || "").trim();
    const countryName = String(club?.country?.name || "").trim();
    if (!countryName || !competitionName) return competitionName;

    const prefix = `${countryName}.`;
    if (competitionName.toLowerCase().startsWith(prefix.toLowerCase())) {
      const withoutCountry = competitionName.slice(prefix.length).trim();
      if (withoutCountry) return withoutCountry;
    }
    return competitionName;
  };

  const getTopStats = async () => {
    try {
      const response = await $apiV2.get(`/stats/top_data`);
      setTopStat(response?.data);
    } catch (error) {
      console.error("Error fetching top stats:", error);
    }
  };

  const getClubStats = async (signal) => {
    try {
      const response = await $apiV1.get(
        `/platform/competition?default=true&page_size=14`,
        signal ? { signal } : undefined
      );
      setClubsList(response?.data.results || []);
      setFilteredClubs(response?.data.results || []);
    } catch (error) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      console.error("Error fetching club stats:", error);
    }
  };

  const handleLeague = async (id) => {
    if (!id) return;
    try {
      const response = await $apiV1.get(
        `/platform/season/${id}`
      );
      setClubsForLeague(response?.data);
      setSelectedCompetitionID(id);
      if (String(searchParams.get("seasonId") || "") !== String(id)) {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set("seasonId", String(id));
        setSearchParams(nextSearchParams);
      }
    } catch (error) {
      console.error("Error fetching league details:", error);
    }
  };

  useEffect(() => {
    getTopStats();
    const ctrl = new AbortController();
    initialLoadAbortRef.current = ctrl;
    getClubStats(ctrl.signal);
    return () => {
      ctrl.abort();
      initialLoadAbortRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!seasonIdFromQuery) return;
    if (String(selectedCompetitionID || "") === String(seasonIdFromQuery)) return;
    handleLeague(seasonIdFromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonIdFromQuery]);

  const getSeasonLink = (seasonId) =>
    seasonId ? `/tournaments?seasonId=${seasonId}` : "/tournaments";
  const getCompetitionLink = (seasonId) =>
    seasonId ? `/tournaments/competition/${seasonId}` : "/tournaments";

  const handleSelectClubSeason = (club, season) => {
    if (!season?.id) return;
    trackEntitySearchResultSelected({
      entity: club,
      entityType: "competition",
      query: searchTerm,
      sourceSurface: "tournaments",
      metadata: {
        season_id: season.id,
        season_label: getSeasonLabel(season),
      },
    });
    setSearchTerm(club?.name || "");
    setShowDropdown(false);
    handleLeague(season.id);
  };

  const handleDropdownLeagueClick = (club) => {
    const preferredSeason = getPreferredSeason(club?.seasons);
    handleSelectClubSeason(club, preferredSeason);
  };

  const handleDropdownSeasonClick = (club, season) => {
    handleSelectClubSeason(club, season);
  };

  const handleSearchChange = async (e) => {
    const { value } = e.target;
    setSearchTerm(value);

    initialLoadAbortRef.current?.abort();
    initialLoadAbortRef.current = null;
    searchAbortRef.current?.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;

    try {
      const response = await $apiV1.get(`/platform/competition`, {
        params: { page: 1, search: value, page_size: 10 },
        signal: ctrl.signal,
      });
      const results = response?.data.results || [];
      setFilteredClubs(results);
      trackEntitySearch({
        entityType: "competition",
        query: value,
        resultCount: response?.data?.count,
        results,
        sourceSurface: "tournaments",
      });
    } catch (error) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      console.error("Error fetching club stats:", error);
    } finally {
      if (searchAbortRef.current === ctrl) searchAbortRef.current = null;
    }
  };

  const handleKeyPress = async (e) => {
    if (e.key !== "Enter") return;
    initialLoadAbortRef.current?.abort();
    initialLoadAbortRef.current = null;
    searchAbortRef.current?.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;

    try {
      const response = await $apiV1.get(`/platform/competition`, {
        params: { search: searchTerm, page_size: 100 },
        signal: ctrl.signal,
      });
      const results = response?.data.results || [];
      setClubsList(results);
      setFilteredClubs(results);
      trackEntitySearch({
        entityType: "competition",
        query: searchTerm,
        resultCount: response?.data?.count,
        results,
        sourceSurface: "tournaments",
        metadata: {
          submit_method: "enter",
        },
      });
    } catch (error) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      console.error("Error fetching club stats:", error);
    } finally {
      if (searchAbortRef.current === ctrl) searchAbortRef.current = null;
    }
  };

  const clearSearch = () => {
    setShowDropdown(false);
    setSearchTerm("");
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat("en-US").format(number);
  };

  const visibleFilteredClubs = filteredClubs.slice(0, 10);

  return (
    <MainWrapper>
      <Navigation />
      <MainWrapperFavorite
        className={
          localStorage.getItem("navigationOpen") === "true"
            ? ""
            : "fullWidthPage"
        }
      >
        <TopWrapper>
          <IntroText
            smallTitle="Hello John"
            title={l(910)}
          />
          <Search />
        </TopWrapper>

        <TopStatisticsWrapper>
          <TopStatCard>
            <NumbersStat>
              <h3>
                {topStat?.count_competiton
                  ? formatNumber(topStat.count_competiton)
                  : topStat?.count_competiton}
              </h3>
              {/* <img src={upIcon} alt={upIcon} /> */}
            </NumbersStat>
            <h5>{l(911)}</h5>
          </TopStatCard>

          <TopStatCard>
            <NumbersStat>
              <h3>
                {topStat?.count_match
                  ? formatNumber(topStat.count_match)
                  : topStat?.count_match}
              </h3>
              {/* <h3> {topStat?.count_match}</h3> */}
              {/* <img src={upIcon} alt={upIcon} /> */}
            </NumbersStat>
            <h5>{l(912)}</h5>
          </TopStatCard>

          <TopStatCard>
            <NumbersStat>
              <h3>
                {topStat?.count_team
                  ? formatNumber(topStat.count_team)
                  : topStat?.count_team}
              </h3>
              {/* <img src={upIcon} alt={upIcon} /> */}
            </NumbersStat>
            <h5>{l(1039)}</h5>
          </TopStatCard>

          <TopStatCard>
            <NumbersStat>
              <h3>
                {topStat?.count_players
                  ? formatNumber(topStat.count_players)
                  : topStat?.count_players}
              </h3>
              {/* <img src={upIcon} alt={upIcon} /> */}
            </NumbersStat>
            <h5>{l(5)}</h5>
          </TopStatCard>
        </TopStatisticsWrapper>

        <ClubsPlayersWrapper>
          <ClubsWrapper>
            <SearchInputWrapper>
              <h5>{l(1)}</h5>
              {/* Searchable Dropdown */}
              <div className="searchable-dropdown">
                <SearhWrap>
                  <img src={searchIcon} alt="search icon" />
                  <input
                    type="text"
                    placeholder={l(913)}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={handleKeyPress}
                  />
                  {showDropdown && searchTerm && (
                    <img
                      className="closeDropdown"
                      src={closeDropdown}
                      alt={closeDropdown}
                      onClick={() => clearSearch()}
                    />
                  )}
                </SearhWrap>
                {showDropdown && searchTerm && (
                  <ul className="dropdown-list">
                    {filteredClubs.length > 0 ? (
                      visibleFilteredClubs.map((club, index) => {
                        const preferredSeason = getPreferredSeason(club?.seasons);
                        const sortedSeasons = getSortedSeasons(club?.seasons);
                        const hasPreferredSeason = Boolean(preferredSeason?.id);
                        return (
                          <li
                            key={club?.id || index}
                            className={`competition-search-item ${hasPreferredSeason ? "" : "disabled"}`}
                          >
                            <SearchItem>
                              <button
                                type="button"
                                className="competition-header"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDropdownLeagueClick(club);
                                }}
                                disabled={!hasPreferredSeason}
                              >
                                <div className="competition-logo">
                                  <img
                                    src={getMediaUrl(club?.country?.flag || club?.logo)}
                                    alt={club?.country?.name || club?.name}
                                  />
                                </div>
                                <div className="competition-info">
                                  <div className="competition-country">
                                    {club?.country?.name || "-"}
                                  </div>
                                  <div className="competition-name">
                                    {getLeagueName(club)}
                                  </div>
                                </div>
                              </button>

                              {sortedSeasons.length > 0 && (
                                <div className="seasons-list">
                                  {sortedSeasons.map((season, seasonIndex) => (
                                    <button
                                      key={season?.id || `${club?.id || index}-${seasonIndex}`}
                                      type="button"
                                      className={`season-action ${
                                        String(preferredSeason?.id) === String(season?.id)
                                          ? "current"
                                          : ""
                                      }`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDropdownSeasonClick(club, season);
                                      }}
                                    >
                                      {getSeasonLabel(season)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </SearchItem>
                            {index < visibleFilteredClubs.length - 1 && (
                              <div className="dropdown-divider" />
                            )}
                          </li>
                        );
                      })
                    ) : (
                      <EmptySearch>
                        <h5>{l(914)}</h5>
                      </EmptySearch>
                    )}
                  </ul>
                )}
              </div>
            </SearchInputWrapper>

            <div style={{
              height: '435px',
              overflowY: 'auto',
            }}>
              {clubsList?.filter(item => item.seasons?.length > 0).map((item, index) => {
                const preferredSeason = getPreferredSeason(item?.seasons);
                return (
                <ClubCard key={index}>
                  <ClubContent>
                    {/* <img src={start} alt={start} /> */}
                    <img src={item.logo} alt={item.logo} className="flag" />
                    <Location>
                      <h5>{item?.country?.name}</h5>
                      <a
                        href={getSeasonLink(preferredSeason?.id)}
                        title={item?.name}
                        className="tournaments"
                        // onClick={() =>
                        //   navigate(
                        //     `/tournaments/competition/${item.seasons[0].id}`
                        //   )
                        // }
                        style={{ cursor: "pointer" }}
                      >
                        {item?.name}
                      </a>
                    </Location>
                  </ClubContent>
                  <button
                    className="seeClubs"
                    onClick={() => handleLeague(preferredSeason?.id)}
                  >
                    {localStorage.getItem("theme") === "light" ? (
                      <img src={next_blue} alt={next_blue} />
                    ) : (
                      <img src={next} alt={next} />
                    )}
                  </button>
                </ClubCard>
              )})}
            </div>
          </ClubsWrapper>

          <TeamsWrapp>
            <TopTable>
              <TableNamesTop>
                <h5>{l(1038)}</h5>
                <h5>{l(5)}</h5>

                <div style={{ width: "92px" }}></div>
              </TableNamesTop>
              {clubsForLeague?.teams?.length > 0 && (
                <Button
                  size={"small"}
                  className="seeTournament"
                  title="see competition"
                  onClick={() => navigate(getCompetitionLink(selectedCompetitionID))}
                  onContextMenu={() =>
                    window.open(
                      getCompetitionLink(selectedCompetitionID),
                      "_blank"
                    )
                  }
                />
              )}
            </TopTable>
            <TeamsWrapper>
              {clubsForLeague?.teams?.length > 0 ? (
                clubsForLeague?.teams?.sort((a, b) => {
                  if (a.name < b.name) {
                    return -1;
                  }
                  if (a.name > b.name) {
                    return 1;
                  }
                  return 0;
                })?.map((item, index) => (
                  <TeamsCard key={index}>
                    <TeamContent
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/tournaments/team/${item.id}`)}
                      onContextMenu={() =>
                        window.open(`/tournaments/team/${item.id}`, "_blank")
                      }
                    >
                      {/* <img src={start} alt={start} /> */}
                      <img src={item.logo} alt={item.logo} className="logo" />
                      <h3>{item.name}</h3>
                    </TeamContent>
                    <PlayersCount>
                      <h3>{item.count_players}</h3>
                    </PlayersCount>
                    <SeeMoreTeam
                      onClick={() => navigate(`/tournaments/team/${item.id}`)}
                      onContextMenu={() =>
                        window.open(`/tournaments/team/${item.id}`, "__blank")
                      }
                    >
                      <h3>{l(963)}</h3>
                    </SeeMoreTeam>
                  </TeamsCard>
                ))
              ) : (
                <EmptyState style={{ height: "100%" }}>
                  <img src={emptyState} alt={emptyState} />
                  <h3>{l(915)}</h3>
                </EmptyState>
              )}
            </TeamsWrapper>
          </TeamsWrapp>
        </ClubsPlayersWrapper>
      </MainWrapperFavorite>
    </MainWrapper>
  );
};

export default Index;
